import { useEffect, useState } from "react";
import api from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AdminInstitutions } from "../components/institution/AdminInstitutions";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Users, DollarSign, FileText, LifeBuoy, Save, Bell, Building2 } from "lucide-react";

const GRANT_OPTS = [["1", "1 month"], ["3", "3 months"], ["6", "6 months"], ["12", "12 months"], ["1200", "Lifetime"]];

function InstitutionBadge({ u }) {
  const lifetime = u.institution_until?.startsWith("2099");
  return (
    <span data-testid={`user-institution-${u.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-sky-100 text-sky-800">
      <Building2 className="w-3.5 h-3.5" /> Institutional · {u.org_name || "Organization"} · {lifetime ? "lifetime" : u.institution_until ? `until ${new Date(u.institution_until).toLocaleDateString("en-GB")}` : "active"}
    </span>
  );
}

const fmt = (d) => new Date(d).toLocaleDateString("en-GB");
const PLAN_NAMES = { monthly: "Monthly", annual: "Yearly" };
const isPaid = (u) => PLAN_NAMES[u.subscription_plan] && ["active", "cancelled"].includes(u.subscription_status) && u.current_period_end && new Date(u.current_period_end) > new Date();

function subLabel(u) {
  const s = u.subscription_status, plan = PLAN_NAMES[u.subscription_plan];
  if (s === "complimentary") return [`Complimentary · ${u.complimentary_until === "lifetime" ? "lifetime" : `until ${fmt(u.complimentary_until)}`}`, "bg-violet-100 text-violet-800"];
  if (s === "trial") return [u.trial_ends_at ? `Trial · ${new Date(u.trial_ends_at) > new Date() ? "ends" : "ended"} ${fmt(u.trial_ends_at)}` : "Trial", "bg-amber-100 text-amber-800"];
  if (s === "active" && plan) return [`${plan} · renews ${fmt(u.current_period_end)}`, "bg-emerald-100 text-emerald-800"];
  if (s === "cancelled" && isPaid(u)) return [`${plan} · cancelled · access until ${fmt(u.current_period_end)}`, "bg-orange-100 text-orange-800"];
  if (s === "active") return [u.subscription_plan === "admin" ? "Admin" : "Active", "bg-emerald-100 text-emerald-800"];
  return [s === "cancelled" ? "Cancelled · no access" : s === "expired" ? "Expired · no access" : s, "bg-slate-100 text-slate-600"];
}

function SubscriptionBadge({ u }) {
  const [text, cls] = subLabel(u);
  return <span data-testid={`user-status-${u.id}`} className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cls}`}>{text}</span>;
}

function GrantAccess({ u, onDone }) {
  const [months, setMonths] = useState("3");
  const grant = async (m) => {
    if (isPaid(u) && !window.confirm(`${u.name || u.email} has a paid ${PLAN_NAMES[u.subscription_plan]} subscription until ${fmt(u.current_period_end)}. Replace it with free access?`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/complimentary`, { months: Number(m), note: "Promotional access" });
      onDone(data);
      toast.success(Number(m) === 0 ? "Free access removed" : "Free access granted");
    } catch {
      toast.error("Could not update access");
    }
  };
  return (
    <div className="flex items-center gap-1.5">
      <select data-testid={`grant-months-${u.id}`} value={months} onChange={(e) => setMonths(e.target.value)} className="h-8 rounded-md border border-slate-200 text-xs px-2 bg-white">
        {GRANT_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <Button size="sm" variant="outline" data-testid={`grant-btn-${u.id}`} onClick={() => grant(months)} className="h-8 text-xs">Grant free access</Button>
      {u.subscription_status === "complimentary" && <Button size="sm" variant="ghost" data-testid={`revoke-btn-${u.id}`} onClick={() => grant(0)} className="h-8 text-xs text-red-600">Revoke</Button>}
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [content, setContent] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [notes, setNotes] = useState({});
  const [tab, setTab] = useState("overview");
  const [newOrgs, setNewOrgs] = useState(0);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
    api.get("/admin/tickets").then(({ data }) => setTickets(data)).catch(() => {});
    api.get("/admin/institutions").then(({ data }) => setNewOrgs(data.organizations.filter((o) => o.status === "New request").length)).catch(() => {});
    api.get("/admin/suggestions").then(({ data }) => setSuggestions(data)).catch(() => {});
    api.get("/admin/content").then(({ data }) => {
      setContent(data.items);
      setNotes(Object.fromEntries(data.items.map((i) => [i.id, i.note || ""])));
    }).catch(() => {});
  }, []);

  const saveNote = async (id) => {
    try {
      await api.put(`/admin/content/${id}`, { note: notes[id] || "" });
      toast.success("Saved");
    } catch { toast.error("Could not save"); }
  };

  const statCards = stats ? [
    { label: "Total users", value: stats.total_users, icon: Users },
    { label: "Active subscriptions", value: stats.active_subscriptions, icon: DollarSign },
    { label: "MRR", value: `$${stats.mrr_usd}`, icon: DollarSign },
    { label: "Total cases", value: stats.total_cases, icon: FileText },
    { label: "Open tickets", value: stats.open_tickets, icon: LifeBuoy },
  ] : [];

  return (
    <div data-testid="admin-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Admin Panel</h1>
      <p className="text-slate-500 mt-1">Subscribers, revenue, support and the clinical content base.</p>

      {newOrgs > 0 && (
        <button type="button" onClick={() => setTab("institutions")} data-testid="admin-new-requests-alert"
          className="mt-5 w-full text-left flex items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900 hover:bg-amber-100 transition-colors">
          <Bell className="w-5 h-5 shrink-0 text-amber-600" />
          <span className="text-sm"><b>{newOrgs} new institutional {newOrgs === 1 ? "request" : "requests"}</b> waiting for a quotation — tap to review.</span>
        </button>
      )}
      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="tab-tickets">Support</TabsTrigger>
          <TabsTrigger value="suggestions" data-testid="tab-suggestions">Suggestions</TabsTrigger>
          <TabsTrigger value="content" data-testid="tab-content">Content</TabsTrigger>
          <TabsTrigger value="institutions" data-testid="tab-institutions">Institutions{newOrgs > 0 && <span data-testid="tab-institutions-badge" className="ml-1.5 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold">{newOrgs}</span>}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-5">
          <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {statCards.map((s) => (
              <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                <s.icon className="w-5 h-5 text-sky-600" />
                <p className="font-heading text-2xl font-bold text-slate-900 mt-2">{s.value}</p>
                <p className="text-sm text-slate-500">{s.label}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-5">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="divide-y divide-slate-100">
              {users.map((u) => (
                <div key={u.id} className="flex items-center justify-between px-5 py-3 gap-4 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 truncate">{u.name} <span className="text-xs text-slate-400">({u.role})</span></p>
                    <p className="text-sm text-slate-500 truncate">{u.email}</p>
                    <p data-testid={`user-agreement-${u.id}`} className={`text-[11px] mt-0.5 font-medium ${u.clinical_agreement_required ? "text-amber-600" : "text-emerald-700"}`}>
                      {u.clinical_agreement_required ? "Clinical agreement: not yet accepted" : `Clinical agreement v${u.clinical_agreement_version} accepted ${new Date(u.clinical_agreement_accepted_at).toLocaleString("en-GB")}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {u.subscription_status === "institutional" ? (
                      <>
                        <InstitutionBadge u={u} />
                        <span className="text-xs text-slate-500" data-testid={`user-managed-by-org-${u.id}`}>Access managed by the organization contract</span>
                      </>
                    ) : (<>
                    <SubscriptionBadge u={u} />
                    {u.role !== "admin" && <GrantAccess u={u} onDone={(nu) => setUsers((xs) => xs.map((x) => (x.id === nu.id ? nu : x)))} />}
                    </>)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="tickets" className="mt-5">
          {tickets.length === 0 ? (
            <p className="text-slate-500">No support tickets.</p>
          ) : (
            <div className="space-y-3">
              {tickets.map((t) => (
                <div key={t.id} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <p className="font-medium text-slate-900">{t.subject}</p>
                    <span className="text-xs bg-sky-50 text-sky-700 px-2 py-0.5 rounded-full">{t.category}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-2">{t.message}</p>
                  <p className="text-xs text-slate-400 mt-2">{t.user_name} · {t.user_email} · {new Date(t.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="suggestions" className="mt-5">
          {suggestions.length === 0 ? (
            <p className="text-slate-500">No suggestions yet.</p>
          ) : (
            <div className="space-y-3">
              {suggestions.map((t) => (
                <div key={t.id} data-testid={`suggestion-${t.id}`} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <p className="font-medium text-slate-900">{t.name}</p>
                    <span className="text-xs bg-violet-50 text-violet-700 px-2 py-0.5 rounded-full">{t.kind}</span>
                  </div>
                  {t.details && <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{t.details}</p>}
                  {t.reference && <p className="text-xs text-slate-500 italic mt-1">Ref: {t.reference}</p>}
                  <p className="text-xs text-slate-400 mt-2">{t.user_name} · <a className="text-sky-700" href={`mailto:${t.user_email}`}>{t.user_email}</a> · {new Date(t.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="content" className="mt-5">
          <p className="text-sm text-slate-500 mb-3">Add editorial notes / pending updates to any module (visible to admins). Core clinical rules are version-controlled and reviewed before release.</p>
          <div className="space-y-3">
            {content.map((c) => (
              <div key={c.id} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p className="font-medium text-slate-900">{c.name}</p>
                    <p className="text-xs text-slate-400">{c.category} · {c.kind}</p>
                  </div>
                </div>
                <Textarea
                  data-testid={`content-note-${c.id}`}
                  value={notes[c.id] || ""}
                  onChange={(e) => setNotes({ ...notes, [c.id]: e.target.value })}
                  rows={2}
                  className="mt-3"
                  placeholder="Editorial note / pending update..."
                />
                <Button size="sm" onClick={() => saveNote(c.id)} data-testid={`content-save-${c.id}`} className="mt-2 bg-slate-900 hover:bg-slate-800">
                  <Save className="w-3.5 h-3.5 mr-1.5" /> Save note
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="institutions" className="mt-5"><AdminInstitutions onCount={setNewOrgs} /></TabsContent>
      </Tabs>
    </div>
  );
}
